import { computed, onMounted, ref, watch } from 'vue';
import { useI18n, usePlanLimits } from '@/composables';

type BotRuleSettingsRule = {
  conditions: Record<string, unknown>;
};

type BotRuleSettingsProps = {
  rule: BotRuleSettingsRule;
};

export const useBotRuleSettings = (props: BotRuleSettingsProps) => {
  const rule = props.rule;
  const { t } = useI18n();
  const planLimitsControl = usePlanLimits();

  const conditions = ref<Rules.condition[]>([]);

  const conditionPlanLimits = (condition: string) =>
    planLimitsControl.conditionPlanLimits(condition);

  const defaultConditionsList = computed<Rules.condition[]>(() => [
    { name: t('Brand and model'), condition: 'brand' },
    { name: t('Browser and version'), condition: 'browser' },
    { name: t('Connection type'), condition: 'connectType' },
    { name: t('Country'), condition: 'country' },
    { name: t('State / region'), condition: 'state' },
    { name: t('City'), condition: 'city' },
    { name: t('Custom token'), condition: 'customToken' },
    { name: t('Query header'), condition: 'queryHeader' },
    { name: t('Custom 1'), condition: 'custom1' },
    { name: t('Custom 2'), condition: 'custom2' },
    { name: t('Custom 3'), condition: 'custom3' },
    { name: t('Custom 4'), condition: 'custom4' },
    { name: t('Custom 5'), condition: 'custom5' },
    { name: t('Custom 6'), condition: 'custom6' },
    { name: t('Custom 7'), condition: 'custom7' },
    { name: t('Custom 8'), condition: 'custom8' },
    { name: t('Custom 9'), condition: 'custom9' },
    { name: t('Custom 10'), condition: 'custom10' },
    { name: t('Day of week'), condition: 'dayOfWeek' },
    { name: t('Time of day'), condition: 'timeOfDay' },
    { name: t('Device type'), condition: 'deviceType' },
    { name: t('IP'), condition: 'ip' },
    { name: t('ISP'), condition: 'isp' },
    { name: t('Language'), condition: 'language' },
    { name: t('Mobile carrier'), condition: 'mobileCarrier' },
    { name: t('OS and version'), condition: 'os' },
    { name: t('Referrer'), condition: 'referrer' },
    { name: t('Referrer Domain'), condition: 'referrerDomain' },
    { name: t('User agent'), condition: 'userAgent' },
    { name: t('WebView'), condition: 'webViewMark' },
    { name: t('Crawler'), condition: 'crawler' },
    { name: t('Proxy'), condition: 'proxy' },
    { name: t('Suspicious Traffic (blacklisted IPs)'), condition: 'ipBlacklisted' },
    { name: t('Suspicious Traffic (data centres IPs)'), condition: 'ipDataCenter' },
    { name: t('Suspicious Traffic (Frequent Events)'), condition: 'ipWithFrequentEvents' },
    {
      name: t('Suspicious Traffic (Frequent Campaign Events)'),
      condition: 'ipWithFrequentCampaignEvents',
    },
    { name: t('Campaign and Visit Geo'), condition: 'visitCountryMatch' },
    { name: t('External ID'), condition: 'externalId' },
  ]);

  const selectedConditions = computed(() =>
    conditions.value.filter((item) => item.selected),
  );

  const notSelectedConditions = computed(() =>
    conditions.value.filter((item) => !item.selected),
  );

  const selectedDictionary = computed(() => {
    const result: Record<string, boolean> = {};
    selectedConditions.value.forEach((item) => {
      result[item.condition] = true;
    });
    return result;
  });

  const selectedDictionaryEmpty = computed(
    () => Object.keys(selectedDictionary.value).length === 0,
  );

  const customConditions = computed(() =>
    Array.from({ length: 10 }, (_, index) => index + 1).filter(
      (index) => selectedDictionary.value[`custom${index}`],
    ),
  );

  const isTimezoneAvailable = computed(
    () => selectedDictionary.value.timeOfDay || selectedDictionary.value.dayOfWeek,
  );

  const isClientHintsNoticeVisible = computed(() =>
    Object.keys(rule.conditions).some((item) =>
      ['os', 'browser', 'deviceType', 'brand'].includes(item),
    ),
  );

  const menuList = computed(() =>
    notSelectedConditions.value.map<Bemob.ListItem>((item) => {
      const planLimits = conditionPlanLimits(item.condition);
      return {
        key: item.condition,
        title: item.name,
        ...(planLimits ? { planLimits } : {}),
        click: () => add(item),
      };
    }),
  );

  const resetConditionsList = () => {
    conditions.value = defaultConditionsList.value.map((item) => ({ ...item }));
  };

  const syncSelectedFromRule = (value: Record<string, unknown> | null | undefined) => {
    if (!value) {
      return;
    }

    const ruleKeys = Object.keys(value);
    conditions.value = conditions.value.map((item) => ({
      ...item,
      selected: ruleKeys.includes(item.condition),
    }));
  };

  const add = (item: Rules.condition) => {
    const index = conditions.value.indexOf(item);
    if (index !== -1) {
      conditions.value[index] = {
        ...conditions.value[index],
        selected: true,
      };
    }
  };

  const remove = (conditionName: string) => {
    const item = conditions.value.find(
      (entry) => entry.selected && entry.condition === conditionName,
    );

    if (item) {
      item.selected = false;
    }

    delete rule.conditions[conditionName];

    if (!isTimezoneAvailable.value && rule.conditions.timezone) {
      delete rule.conditions.timezone;
    }
  };

  const deselectAll = () => {
    conditions.value.forEach((item) => {
      item.selected = false;
    });
    rule.conditions = {};
  };

  const resetValidation = () => {};

  watch(
    () => props.rule.conditions,
    (value) => {
      syncSelectedFromRule(value);
    },
    { immediate: true, deep: true },
  );

  onMounted(() => {
    resetConditionsList();
    syncSelectedFromRule(props.rule.conditions);
  });

  return {
    t,
    conditions,
    menuList,
    selectedDictionary,
    selectedDictionaryEmpty,
    customConditions,
    isTimezoneAvailable,
    isClientHintsNoticeVisible,
    remove,
    deselectAll,
    resetValidation,
  };
};
